import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading2,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'
import { LandingPageButton } from '../../../../components'
import { BoxPricesTable } from '../../../../components/BoxPricesTable'
import type { HowGoustoFoodBoxesWorkTypes } from '../../../../types'
import { MealPlanningSections, TargetUri, TrackingActions } from '../../../../types/tracking'
import { GOUSTO_COST } from './config'

export function HowMuchDoesGoustoCost({ startOnboarding }: HowGoustoFoodBoxesWorkTypes) {
  const existingPricing = {
    pricePerPortion: '3.09',
    discountedPricePerPortion: '1.54',
  }

  const newPricing = useMarketingPricingConfig()

  const { pricePerPortion, discountedPricePerPortion } = newPricing || existingPricing

  return (
    <Box
      display={Display.Flex}
      bg={Color.NeutralGrey_50}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
      paddingH={[8, 20]}
      paddingV={16}
      paddingBottom={6}
      gap={[4, 8]}
    >
      <Box width={['null', null, null, '600px']} paddingBottom={[3, 4]}>
        <Heading2>{GOUSTO_COST.headingLine1}</Heading2>
        <Space size={3} />
        <Body2 size={4}>The price of your recipe box will depend on:</Body2>
        <ul>
          <li>
            <Body2 size={4}>How many recipes you choose for the week</Body2>
          </li>
          <li>
            <Body2 size={4}>
              Whether you want those meals for 1 person, 2 people, or up to 5 people
            </Body2>
          </li>
        </ul>

        <Body2 size={4}>
          The more recipes that you order, the less you&apos;ll pay per portion, with prices
          starting at <s>£{pricePerPortion}</s> £{discountedPricePerPortion}
          <sup>‡</sup> per serving. For more information, visit our{' '}
          <LandingPageButton
            startOnboarding={startOnboarding}
            sectionName={MealPlanningSections.HOW_MUCH_DOES_GOUSTO_COST}
            target={TargetUri.BOX_PRICES}
            action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
            asLink
          >
            box prices page.
          </LandingPageButton>
        </Body2>
        <Space size={4} />

        <Body2 size={4}>
          Learn more on{' '}
          <LandingPageButton
            startOnboarding={startOnboarding}
            sectionName={MealPlanningSections.HOW_MUCH_DOES_GOUSTO_COST}
            target={TargetUri.HOW_GOUSTO_FOOD_BOXES_WORK}
            action={TrackingActions.HOW_GOUSTO_WORKS}
            asLink
          >
            how Gousto recipes boxes work
          </LandingPageButton>{' '}
          to get the most out of your weekly meal planning for households as small as 1 person to a
          large family of 5.
        </Body2>
        <Space size={4} />

        <Body2 size={4}>
          We have created a handy breakdown in the table to show you the price of Gousto meals per
          serving. This should help you figure out which flexible recipe box option is right for you
          and your household week on week. All prices include our new customer introductory discount
          with free delivery on your first box.
        </Body2>
        <Space size={4} />

        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
        >
          <LandingPageButton
            startOnboarding={startOnboarding}
            sectionName={MealPlanningSections.HOW_MUCH_DOES_GOUSTO_COST}
            target={TargetUri.SIGNUP}
            action={TrackingActions.CLICK_GET_STARTED}
          >
            {GOUSTO_COST.ctaText}
          </LandingPageButton>
        </Box>
      </Box>
      <Box width={['100%', null, null, '600px']} paddingBottom={[3, 4]}>
        <BoxPricesTable />
      </Box>
    </Box>
  )
}
